import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, SimpleLoading, Text } from "@core/index";
import Modal from "@core/modal";
import {
  ArrowLeft,
  Clock,
  Link2,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  User,
  MessageSquare,
  Volume2,
  Play,
  Pause,
} from "lucide-react";
import { useGetSpokenDialogue } from "@packages/api/madlenAPI"; // Fixed import
import ErrorComponent from "@features/tools/components/error-component";
import { getVoiceById } from "@packages/config/elevenlabsConfig";

const DetailSpokenDialogueView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [copying, setCopying] = useState(false);
  const [expandedStudents, setExpandedStudents] = useState<
    Record<string, boolean>
  >({});
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [modalConversationTurns, setModalConversationTurns] = useState<any[]>(
    [],
  );
  const [modalParticipantName, setModalParticipantName] = useState<
    string | null
  >(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data, isLoading, error } = useGetSpokenDialogue(Number(id), {
    query: {
      enabled: !!id,
      refetchInterval: (data: any) => {
        const s = (data as any)?.status as string | undefined;
        if (s === "READY" || s === "FAILED") return false;
        return 5000;
      },
      refetchOnWindowFocus: false,
    },
  });

  // Cleanup audio when component unmounts - moved to top to fix hooks order
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const formatRelativeTime = (input?: string | number | Date) => {
    if (!input) return "";
    const date = new Date(input);
    if (isNaN(date.getTime())) return "";
    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" });
    const units: [Intl.RelativeTimeFormatUnit, number][] = [
      ["year", 60 * 60 * 24 * 365],
      ["month", 60 * 60 * 24 * 30],
      ["week", 60 * 60 * 24 * 7],
      ["day", 60 * 60 * 24],
      ["hour", 60 * 60],
      ["minute", 60],
      ["second", 1],
    ];
    for (const [unit, sec] of units) {
      if (Math.abs(diffSeconds) >= sec || unit === "second") {
        const value = Math.round(diffSeconds / sec);
        return rtf.format(value, unit);
      }
    }
    return "";
  };

  const toggleStudentExpanded = (participantName: string) => {
    setExpandedStudents((prev) => ({
      ...prev,
      [participantName]: !prev[participantName],
    }));
  };

  // open conversation modal
  const openConversationModal = (participantName: string, turns: any[]) => {
    setModalParticipantName(participantName);
    setModalConversationTurns(turns || []);
    setIsConversationModalOpen(true);
  };

  function scoreColor(value?: number | null): string {
    if (typeof value !== "number") return "#9CA3AF";
    if (value < 60) return "#EF4444";
    if (value < 80) return "#F59E0B";
    return "#10B981";
  }

  function ScoreRing({
    value,
    size = 40,
    track = "#E5E7EB",
  }: {
    value?: number | null;
    size?: number;
    track?: string;
  }) {
    const normalized = Math.max(
      0,
      Math.min(100, typeof value === "number" ? value : 0),
    );
    const color = scoreColor(value ?? null);
    const bg = `conic-gradient(${color} ${normalized * 3.6}deg, ${track} 0deg)`;
    return (
      <div
        className="relative flex items-center justify-center rounded-full"
        style={{ width: size, height: size, backgroundImage: bg }}
        title={`Total Score: ${typeof value === "number" ? value : "-"}`}
        aria-label={`Total Score: ${typeof value === "number" ? value : "-"}`}
      >
        <div className="absolute inset-1 rounded-full bg-white dark:bg-dark-600" />
        <div className="relative text-xs font-semibold" style={{ color }}>
          {typeof value === "number" ? Math.round(value) : "-"}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <SimpleLoading fastLoading />;
  }

  if (error || !data) {
    return (
      <ErrorComponent
        title={t("common.error")}
        description={t("common.error")}
      />
    );
  }

  const detail = (data as any) || {};
  const title = detail?.title || detail?.scenario || "";
  const description = detail?.description || "";
  const scenario = detail?.scenario || "";
  const language = detail?.language || "en";
  const accent = detail?.accent || "AMERICAN";
  const voiceId = detail?.voiceId || detail?.voice_id;
  const duration = detail?.duration || 6;
  const cefrLevel = detail?.cefrLevel || "";
  // removed weights since it's unused
  const status = (detail?.status as string | undefined) || undefined;
  const publicCode = detail?.publicCode;
  const imageUrl = detail?.imageUrl;
  const createdAt = detail?.createdAt;
  const studentEvents = detail?.studentEvents || [];

  const publicUrl = publicCode
    ? `${window.location.origin}/content/spoken-dialogue/public/${publicCode}`
    : "";

  // Get voice information
  const voice = voiceId ? getVoiceById(voiceId) : null;

  const handlePlayVoice = () => {
    if (!voice) return;

    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // If clicking the same voice that's playing, stop it
    if (playingVoiceId === voice.voiceId) {
      setPlayingVoiceId(null);
      return;
    }

    // Start new audio
    const audio = new Audio(voice.voiceSamplePath);
    audioRef.current = audio;
    setPlayingVoiceId(voice.voiceId);

    audio.addEventListener("ended", () => {
      setPlayingVoiceId(null);
    });

    audio.play().catch(console.error);
  };

  const statusColor = (status?: string) => {
    switch (status) {
      case "READY":
        return "text-green-600 bg-green-100 dark:text-green-300 dark:bg-green-900/30";
      case "PROCESSING":
        return "text-orange-600 bg-orange-100 dark:text-orange-300 dark:bg-orange-900/30";
      case "FAILED":
        return "text-red-600 bg-red-100 dark:text-red-300 dark:bg-red-900/30";
      default:
        return "text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-900/30";
    }
  };

  const statusText = (status?: string) => {
    switch (status) {
      case "READY":
        return t("spokenDialogue.status.ready");
      case "PROCESSING":
        return t("spokenDialogue.status.processing");
      case "FAILED":
        return t("spokenDialogue.status.failed");
      default:
        return t("spokenDialogue.status.unknown");
    }
  };

  return (
    <div className="flex-1 p-4 bg-gray-50 dark:bg-dark-500 overflow-y-auto h-full">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Button
            onClick={() => navigate("/content/spoken-dialogue")}
            size="md"
            rounded="full"
            variant="outline"
            leftIcon={<ArrowLeft size={18} />}
          >
            {t("common.back")}
          </Button>
        </div>

        {/* Header Card with Title and Description */}
        <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 mb-4 overflow-hidden">
          {imageUrl && (
            <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <img
                src={imageUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <Text variant="h1" className="text-2xl font-semibold mb-2">
                      {title && String(title).trim().length > 0
                        ? title
                        : t("spokenDialogue.create.basicInfo.titlePlaceholder")}
                    </Text>
                    {description && (
                      <Text
                        variant="p"
                        className="text-gray-600 dark:text-gray-300 mb-2"
                      >
                        {description}
                      </Text>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {status && (
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${statusColor(status)}`}
                      >
                        {status === "PROCESSING" && (
                          <div className="inline-block w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-2" />
                        )}
                        {statusText(status)}
                      </span>
                    )}
                  </div>
                </div>
                {createdAt && (
                  <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    {formatRelativeTime(createdAt)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Details (single column to make Settings full width) */}
        <div className="grid grid-cols-1 gap-4 mb-4">
          {/* Language & Settings (expanded) */}
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <div className="space-y-3">
              {scenario && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    {t("spokenDialogue.detail.scenario")}:
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400 text-right max-w-md">
                    {scenario}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  {t("interactive_material.create.contentLanguage")}:
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {language === "en" &&
                      t("interactive_material.create.contentLanguages.en")}
                    {language === "tr" &&
                      t("interactive_material.create.contentLanguages.tr")}
                    {language === "de" &&
                      t("interactive_material.create.contentLanguages.de")}
                    {language === "fr" &&
                      t("interactive_material.create.contentLanguages.fr")}
                    {language === "it" &&
                      t("interactive_material.create.contentLanguages.it")}
                    {language === "es" &&
                      t("interactive_material.create.contentLanguages.es")}
                  </span>
                  {accent && language === "en" && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {accent === "BRITISH" &&
                          t("spokenDialogue.detail.accent.british", "British")}
                        {accent === "AMERICAN" &&
                          t(
                            "spokenDialogue.detail.accent.american",
                            "American",
                          )}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {voice && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    {t("voiceSelector.voice")}:
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <Volume2 className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                      </div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {voice.displayName}
                      </span>
                    </div>
                    <button
                      onClick={handlePlayVoice}
                      className="flex items-center gap-1.5 px-3 py-1 text-sm rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-500"
                    >
                      {playingVoiceId === voice.voiceId ? (
                        <>
                          <Pause className="w-3.5 h-3.5" />
                          <span>Stop</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-3.5 h-3.5" />
                          <span>Preview</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              {cefrLevel && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">
                    {t("spokenDialogue.detail.level")}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {cefrLevel}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {t("spokenDialogue.detail.durationLabel")}
                </span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {t("spokenDialogue.detail.duration.dialogs", {
                    count: duration,
                  })}
                </span>
              </div>

              {/* Move prompt/sample into settings to make it longer */}
              {detail?.livekitPrompt && (
                <div className="pt-4 border-t border-gray-100 dark:border-dark-400">
                  <Text
                    variant="p"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    <span className="font-medium">
                      {t("spokenDialogue.detail.livekitPrompt")}:{" "}
                    </span>
                    <span className="block text-gray-800 dark:text-gray-200 whitespace-pre-wrap mt-1">
                      {detail.livekitPrompt}
                    </span>
                  </Text>
                </div>
              )}
              {detail?.livekitSample && (
                <div>
                  <Text
                    variant="p"
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    <span className="font-medium">
                      {t("spokenDialogue.detail.livekitSample")}:{" "}
                    </span>
                    <span className="block text-gray-800 dark:text-gray-200 whitespace-pre-wrap mt-1">
                      {detail.livekitSample}
                    </span>
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Student Results */}
        {(() => {
          const filteredStudentEvents = (studentEvents || []).filter(
            (se: any) => {
              const events = se?.events || [];
              const evaluationEvent = events.find(
                (event: any) => event.title === "dialogue_evaluation",
              );
              const evaluation = evaluationEvent?.data;
              const score = evaluation?.total_score;
              // keep only when score is a valid number or numeric string
              return (
                typeof score === "number" ||
                (score !== null &&
                  score !== undefined &&
                  String(score).trim() !== "" &&
                  !isNaN(Number(score)))
              );
            },
          );

          if (filteredStudentEvents.length === 0) return null;

          return (
            <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6 mb-4">
              <Text variant="h2" className="text-lg font-semibold mb-4">
                Student Results ({filteredStudentEvents.length})
              </Text>
              <div className="divide-y divide-gray-200 dark:divide-dark-400">
                {filteredStudentEvents.map((studentEvent: any) => {
                  const participantName =
                    studentEvent?.participantName || "Unknown";
                  const events = studentEvent?.events || [];
                  const isStudentExpanded = expandedStudents[participantName];
                  // Find evaluation data
                  const evaluationEvent = events.find(
                    (event: any) => event.title === "dialogue_evaluation",
                  );
                  const evaluation = evaluationEvent?.data;

                  // Extract conversation turns
                  const conversationTurns = events.filter(
                    (event: any) => event.title === "conversation_turn",
                  );

                  return (
                    <div key={studentEvent.id} className="py-3">
                      <button
                        type="button"
                        onClick={() => toggleStudentExpanded(participantName)}
                        className="w-full grid grid-cols-[auto,1fr,auto] items-center gap-3 text-left"
                      >
                        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-sm font-semibold">
                          <User className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {participantName}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {conversationTurns.length} messages •{" "}
                            {new Date(
                              studentEvent.createdAt,
                            ).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {evaluation && (
                            <div className="flex items-center gap-2">
                              <ScoreRing
                                value={evaluation.total_score}
                                size={32}
                              />
                              <span className="hidden sm:inline text-xs text-gray-600 dark:text-gray-300">
                                Total Score
                              </span>
                            </div>
                          )}
                          {isStudentExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                        </div>
                      </button>

                      {isStudentExpanded && evaluation && (
                        <div className="mt-3 pl-11 space-y-3">
                          {/* Evaluation Summary */}
                          <div className="border rounded-lg p-4 dark:border-dark-400 bg-gray-50 dark:bg-dark-500">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                Evaluation Results
                              </h4>
                              <ScoreRing
                                value={evaluation.total_score}
                                size={32}
                              />
                            </div>

                            {/* Category Scores */}
                            {evaluation.category_scores && (
                              <div className="grid grid-cols-1 gap-2 mb-3">
                                {Object.entries(evaluation.category_scores)
                                  .filter(
                                    ([, score]) =>
                                      typeof score === "number" ||
                                      (score !== null &&
                                        score !== undefined &&
                                        String(score).trim() !== "" &&
                                        !isNaN(Number(score))),
                                  )
                                  .map(([key, score]: [string, any]) => {
                                    const displayScore =
                                      typeof score === "number"
                                        ? score
                                        : Number(score);
                                    const percentage =
                                      (displayScore / 20) * 100;

                                    const getProgressColor = (
                                      score: number,
                                    ) => {
                                      if (score >= 16) return "bg-green-500";
                                      if (score >= 12) return "bg-yellow-500";
                                      return "bg-red-500";
                                    };

                                    return (
                                      <div
                                        key={key}
                                        className="flex items-center justify-between py-1"
                                      >
                                        <span className="text-xs text-gray-600 dark:text-gray-400 capitalize flex-1">
                                          {key.replace(/_/g, " ")}
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                            <div
                                              className={`h-1.5 rounded-full ${getProgressColor(displayScore)}`}
                                              style={{
                                                width: `${percentage}%`,
                                              }}
                                            />
                                          </div>
                                          <span className="text-xs font-medium text-gray-900 dark:text-gray-100 w-8 text-right">
                                            {displayScore}/20
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}

                            {/* Message Count Info */}
                            <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 mb-3">
                              <span>
                                User: {evaluation.user_message_count || 0}
                              </span>
                              <span>
                                Total: {evaluation.total_message_count || 0}
                              </span>
                            </div>

                            {/* Strengths */}
                            {evaluation.strengths &&
                              evaluation.strengths.length > 0 && (
                                <div className="mb-3">
                                  <h5 className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">
                                    Strengths
                                  </h5>
                                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                    {evaluation.strengths.map(
                                      (strength: string, idx: number) => (
                                        <li
                                          key={idx}
                                          className="flex items-start gap-2"
                                        >
                                          <span className="text-green-500 mt-0.5">
                                            •
                                          </span>
                                          <span>{strength}</span>
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}

                            {/* Areas for Improvement */}
                            {evaluation.areas_for_improvement &&
                              evaluation.areas_for_improvement.length > 0 && (
                                <div className="mb-3">
                                  <h5 className="text-sm font-medium text-orange-700 dark:text-orange-300 mb-2">
                                    Areas for Improvement
                                  </h5>
                                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                                    {evaluation.areas_for_improvement.map(
                                      (area: string, idx: number) => (
                                        <li
                                          key={idx}
                                          className="flex items-start gap-2"
                                        >
                                          <span className="text-orange-500 mt-0.5">
                                            •
                                          </span>
                                          <span>{area}</span>
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}

                            {/* Overall Feedback */}
                            {evaluation.overall_feedback && (
                              <div className="mb-3">
                                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Overall Feedback
                                </h5>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                  {evaluation.overall_feedback}
                                </p>
                              </div>
                            )}

                            {/* Conversation History Button */}
                            {conversationTurns.length > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  openConversationModal(
                                    participantName,
                                    conversationTurns,
                                  )
                                }
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                              >
                                <MessageSquare className="w-4 h-4" />
                                <span>
                                  View Conversation History (
                                  {conversationTurns.length} messages)
                                </span>
                                <ChevronRight className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Conversation History is shown in modal */}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {publicUrl && (
          <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 p-6">
            <Text variant="h2" className="text-lg font-semibold mb-3">
              {t("spokenDialogue.create.share.title")}
            </Text>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-dark-500 border border-gray-200 dark:border-dark-400">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 flex items-center justify-center">
                <Link2 className="w-4 h-4" />
              </div>
              <input
                type="text"
                readOnly
                value={publicUrl}
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none dark:bg-dark-500 dark:border-dark-400 dark:text-white border-gray-300"
              />
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Copy className="w-4 h-4" />}
                onClick={async () => {
                  try {
                    setCopying(true);
                    await navigator.clipboard.writeText(publicUrl);
                    // Reset after 2 seconds
                    setTimeout(() => setCopying(false), 2000);
                  } catch {
                    setCopying(false);
                  }
                }}
                className={
                  copying
                    ? "bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300"
                    : ""
                }
              >
                {copying ? (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    {t("common.copied")}
                  </span>
                ) : (
                  t("common.copy")
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<ExternalLink className="w-4 h-4" />}
                onClick={() => window.open(publicUrl, "_blank")}
              >
                {t("common.open")}
              </Button>
            </div>
          </div>
        )}

        {/* Resources removed (duplicates of info now inside Settings) */}
      </div>
      {/* Conversation Modal */}
      <Modal
        show={isConversationModalOpen}
        onClose={() => setIsConversationModalOpen(false)}
        title={`Conversation History${modalParticipantName ? ` - ${modalParticipantName}` : ""}`}
      >
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {modalConversationTurns.map((turn: any, idx: number) => (
            <div
              key={idx}
              className={`p-3 rounded-lg ${turn.data.role === "user" ? "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500" : "bg-gray-50 dark:bg-gray-800 border-l-4 border-gray-500"}`}
            >
              <div className="flex items-start justify-between mb-1">
                <span
                  className={`text-xs font-medium ${turn.data.role === "user" ? "text-blue-700 dark:text-blue-300" : "text-gray-700 dark:text-gray-300"}`}
                >
                  {turn.data.role === "user"
                    ? modalParticipantName
                    : "Assistant"}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(turn.data.timestamp).toLocaleTimeString()}
                </span>
              </div>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {turn.data.content}
              </p>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default DetailSpokenDialogueView;
